import { Component, OnInit, ViewChild, ElementRef, OnDestroy, AfterViewInit, Input } from '@angular/core';
import { Subject } from 'rxjs';
import { NavParams, LoadingController, DomController } from '@ionic/angular';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { GUIService } from '../../gui.service';
import { SAMService } from 'src/app/sam/sam.service';
import { ConfigService } from 'src/app/config.service';
import { SearchDialogService } from './search-dialog.service';
import { ThemeService } from 'ngx-elvis-ds';
import { BasePage } from 'ngx-elvis-ds';
import { I18NService } from 'src/app/services/i18n.service';
import { WindowRef } from '../../../window-ref.service';

export class SearchDialogCommands {
  public static OPEN = ['SEARCH_DIALOG_OPEN'];
  public static UPDATE = ['SEARCH_UPDATE', 'STORE_UPDATE'];
  public static CLOSE = ['SEARCH_DIALOG_CLOSE'];
}

@Component({
  selector: 'app-search-dialog',
  templateUrl: './search-dialog.page.html',
  styleUrls: ['./search-dialog.page.scss'],
})
export class SearchDialogPage extends BasePage implements OnInit, OnDestroy {

  @ViewChild('searchDialog', { static: true }) searchDialog: ElementRef;
  @Input() data: any;
  theme: string;

  isMobile: boolean = false;
  reasonData: any;
  modelChanged: Subject<string> = new Subject<string>();

  constructor(
    private loadingCtrl: LoadingController,
    private sam: SAMService,
    public config: ConfigService,
    public themeSvc: ThemeService,
    public guiSvc: GUIService,
    public search: SearchDialogService,
    public i18nSvc: I18NService,
    private domCtrl: DomController,
    private winRef: WindowRef
  ) {
    super();
    this.subscribeModelChange();
  }

  changed(text: string) {
    if (!this.config.guiSettings.disableAsyncSearch) {
      this.modelChanged.next(text);
    }
  }

  ngOnInit() {
    this.search.updateSearchData(this.data, false);
    this.subscribeDataChange();

    this.isMobile = window.innerWidth <= 768;

    if (this.search.searchData.Reasons && this.search.searchData.Reasons[0]) {
      this.reasonData = this.search.searchData.Reasons[0].Id;
    }
  }

  ionViewDidEnter() {
    this.domCtrl.write(() => {
      if (this.search.searchData.SearchOff !== '1') {
        this.guiSvc.initLocalKeyboard('searchDialogForm');
      }
    });
  }

  ngOnDestroy() {
    if (this.subscriptions.modalEventsListener) { this.subscriptions.modalEventsListener.unsubscribe(); }
    if (this.subscriptions.inputChanged) { this.subscriptions.inputChanged.unsubscribe(); }
    if (this.subscriptions.dataChanged) { this.subscriptions.dataChanged.unsubscribe(); }
  }

  onCallback(success) {
    if (success) {
      let parms;
      if (this.search.searchData.MultiSelect) {
        parms = {
          SelectedLines: this.search.searchResults.filter(r => r.selected).map(r => ({ val: String(r.index) })),
        };
      }
      if (this.reasonData) {
        const reason = this.search.searchData.Reasons.find(r => r.Id === this.reasonData);
        if (reason) {
          parms = { ...parms, Reason: reason.Id, Descr: reason.Descr };
        }
      }
      this.sam.onSend({
        Command: this.search.searchData.CallbackAction,
        parms: parms,
        Val: ''
      });
    } else {
      if (this.search.searchData.Callback) {
        this.sam.onSend({
          Command: this.search.searchData.Callback
        });
      }
    }
  }

  onSearchTypeChange(st: any) {
    for (const s of this.search.searchTypes) {
      s.current = false;
    }
    st.current = true;
    this.search.searchTerm = '';
    this.search.searchHeaders = [];
    this.search.searchResults = [];
    this.search.onSearch('EMPTY');
  }

  onSubmit() { 
    // please do not remove me
  }


  onResultSelect(result: any) {

    if (this.winRef.scrolling) {
      return;
    }

    if (this.search.searchData.MultiSelect) {
      result.selected = !result.selected;
      return;
    }

    // default case

    if (
      this.search.searchData.SelectOff !== '1' &&
      this.search.searchData.CallbackAction &&
      this.search.searchData.Type !== 'LIST_ID' &&
      this.search.searchData.Type !== 'JOURNAL'
    ) {
      this.sam.onSend({
        Command: this.search.searchData.CallbackAction,
        Val: String(result.index),
        parms: {
          custCode: String(result.index),
          SearchType: this.search.searchData.SearchType
        }
      });
    }

    // special cases

    const specialCallbacks: string[] = ['VELOCITY_CODE', 'CUSTOMERDETAIL'];

    if (this.search.searchData.Type === 'LIST_ID' && specialCallbacks.includes(this.search.searchData.SearchType) === true) {
      this.sam.onSend({
        Command: this.search.searchData.CallbackAction,
        Val: String(result.index),
        parms: { SearchType: this.search.searchData.SearchType }
      });
    }

    if (this.search.searchData.Type === 'LIST_ID' && specialCallbacks.includes(this.search.searchData.SearchType) === false) {
      let parms;
      if (this.reasonData) {
        const reason = this.search.searchData.Reasons.find(r => r.Id === this.reasonData);
        if (reason) {
          parms = { Reason: reason.Id, Descr: reason.Descr };
        }
      }
      this.sam.onSend({
        Command: 'MAINT_SELECT',
        Val: String(result.index),
        parms: parms
      });
    }

    if (this.search.searchData.Type === 'JOURNAL') {
      this.sam.onSend({
        Command: 'DETAIL_XACT',
        parms: {
          Store: result['data1'].slice(0, 5),
          Pos: result['data1'].slice(5, 10),
          Xact: result['data1'].slice(10, 15),
          Opid: result['data3'].toString(),
          Date: result['data2'].toString(),
        }
      });
    }
    if (!this.search.searchData.SearchType && this.search.searchData.Type === 'ALL' && this.search.currentSearchType === '0') {
      this.sam.onSend({
        Command: 'LOOKUP_SEARCHDLG',
        Val: String(result.data1),
      });
    }
    if (!this.search.searchData.SearchType && this.search.searchData.Type === 'ALL' && this.search.currentSearchType === '1') {
      this.sam.onSend({
        Command: 'DEPT_PLU_SEARCHDLG',
        Val: String(result.data1)
      });
    }
    if (!this.search.searchData.SearchType && this.search.searchData.Type === 'ALL' && this.search.currentSearchType === '5') {
      this.sam.onSend({
        Command: 'CUSTOMER_SEARCHDLG',
        Val: String(result.data1),
        parms: {
          custCode: String(result.data1),
          SearchType: '5'
        }
      });
    }
  }

  // private

  private initKeyboard = (evt: any)  => {
    const event = evt || window.event;
    const key = event.key;

    if (key && (key === 'Escape' || key === 'Esc')) { this.onCallback(false); }
    // if (key && (key === 'Enter')) { this.onSearch(this.searchTerm); }

    return false;
  }

  private async subscribeDataChange() {
    this.subscriptions.dataChanged = this.search.events.subscribe(async () => {
      if (this.search.pageLoader) {
        await this.search.pageLoader.dismiss();
        delete this.search.pageLoader;
      }
    });
  }

  private subscribeModelChange() {
    const time = this.config.guiSettings.disableAsyncSearch ? 0 : (this.config.guiSettings.searchDebounceTime || 500);
    this.subscriptions.inputChanged = this.modelChanged.pipe(debounceTime(time)).pipe(distinctUntilChanged()).subscribe((model) => {
      this.search.searchTerm = model;
      if (!this.config.guiSettings.disableAsyncSearch) { this.search.onSearch(model); }
    });
  }
}
